import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getGroupsList,
  deleteGroup,
  activateGroup,
  updateOrderGroup,
  clearMembersGroup,
  createGroup,
  updateGroup,
} from "../../../../../../../services/groupServices";

import { getDojos } from "../../../../../../../services/dojoServices";
import { getSettings, updateQuotaSettings } from "../../../../../../../services/settingsServices";
import { getAllEnseignants } from "../../../../../../../services/adherentServices";

import {
  calculateGroupsStats,
  DEFAULT_DISCIPLINES,
  filterGroups,
  getDuplicateGroupData,
  getNextGroupOrder,
  sortGroupsByOrder,
  toBool,
  toNumber,
  validateGroupPayload,
} from "../utils/groupsSettingsUtils";

export function useGroupsSettings() {
  const [groups, setGroups] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [enseignants, setEnseignants] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const [isQuotaActive, setIsQuotaActive] = useState(false);
  const [quotaUpdating, setQuotaUpdating] = useState(false);

  const [filters, setFilters] = useState({
    query: "",
    dojoId: "",
    status: "all",
    quotaState: "all",
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [drawerGroup, setDrawerGroup] = useState(null);
  const [submittingDrawer, setSubmittingDrawer] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [clearingId, setClearingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getGroupsList();
      setGroups(sortGroupsByOrder(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error("Erreur chargement groupes :", error);
      toast.error("Impossible de charger les groupes.");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      setLoadingMeta(true);

      const [dojosData, enseignantsData, settingsData] = await Promise.all([
        getDojos(),
        getAllEnseignants(),
        getSettings(),
      ]);

      setDojos(Array.isArray(dojosData) ? dojosData : []);
      setEnseignants(Array.isArray(enseignantsData) ? enseignantsData : []);

      const settings = Array.isArray(settingsData) ? settingsData[0] : settingsData;
      setIsQuotaActive(toBool(settings?.isquotaactive));
    } catch (error) {
      console.error("Erreur chargement données groupes :", error);
      toast.error("Impossible de charger toutes les données des groupes.");
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchGroups(), fetchMeta()]);
  }, [fetchGroups, fetchMeta]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const filteredGroups = useMemo(() => {
    return filterGroups(groups, filters);
  }, [groups, filters]);

  const stats = useMemo(() => calculateGroupsStats(groups), [groups]);
  const filteredStats = useMemo(
    () => calculateGroupsStats(filteredGroups),
    [filteredGroups]
  );

  const nextOrder = useMemo(() => {
    return getNextGroupOrder(groups, filters.dojoId);
  }, [groups, filters.dojoId]);

  const disciplineSuggestions = useMemo(() => {
    const fromGroups = groups
      .map((group) => String(group?.discipline_groupe || "").trim())
      .filter(Boolean);

    return [...new Set([...DEFAULT_DISCIPLINES, ...fromGroups])].sort((a, b) =>
      a.localeCompare(b, "fr")
    );
  }, [groups]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      query: "",
      dojoId: "",
      status: "all",
      quotaState: "all",
    });
  };

  const handleToggleQuota = async () => {
    const next = !isQuotaActive;

    const confirmed = window.confirm(
      next
        ? "Activer le blocage automatique des inscriptions lorsque le quota d’un groupe est atteint ?"
        : "Désactiver le blocage automatique des inscriptions lorsque le quota d’un groupe est atteint ?"
    );

    if (!confirmed) return;

    try {
      setQuotaUpdating(true);

      const response = await updateQuotaSettings(next);
      const value = toBool(response?.isquotaactive ?? next);

      setIsQuotaActive(value);

      toast.success(
        value
          ? "Quota activé : les inscriptions seront bloquées au quota."
          : "Quota désactivé : les inscriptions resteront possibles au-delà du quota."
      );
    } catch (error) {
      console.error("Erreur modification quota :", error);
      toast.error("Impossible de modifier le paramètre quota.");
    } finally {
      setQuotaUpdating(false);
    }
  };

  const openCreateDrawer = () => {
    setDrawerMode("create");
    setDrawerGroup(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (group) => {
    setDrawerMode("edit");
    setDrawerGroup(group);
    setDrawerOpen(true);
  };

  const openDuplicateDrawer = (group) => {
    setDrawerMode("duplicate");
    setDrawerGroup(getDuplicateGroupData(group));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (submittingDrawer) return;

    setDrawerOpen(false);
    setDrawerMode("create");
    setDrawerGroup(null);
  };

  const handleSubmitGroup = async (payload) => {
    const error = validateGroupPayload(payload);

    if (error) {
      toast.error(error);
      return;
    }

    try {
      setSubmittingDrawer(true);

      if (drawerMode === "edit") {
        if (!drawerGroup?.id) {
          toast.error("Groupe introuvable.");
          return;
        }

        await updateGroup({
          ...payload,
          id: drawerGroup.id,
        });

        toast.success("Groupe mis à jour avec succès.");
      } else {
        await createGroup({
          ...payload,
          id: undefined,
        });

        toast.success(
          drawerMode === "duplicate"
            ? "Groupe dupliqué avec succès."
            : "Groupe créé avec succès."
        );
      }

      await fetchGroups();
      closeDrawer();
    } catch (error) {
      console.error("Erreur enregistrement groupe :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de l’enregistrement du groupe."
      );
    } finally {
      setSubmittingDrawer(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!group?.id) return;

    if (toNumber(group.nombreAdherents, 0) > 0) {
      toast.error("Impossible de supprimer un groupe avec des membres actifs.");
      return;
    }

    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le groupe « ${group.nom || "sans nom"} » ?`
    );

    if (!confirmed) return;

    try {
      setDeletingId(group.id);

      await deleteGroup(group.id);

      toast.success("Groupe supprimé avec succès.");
      await fetchGroups();
    } catch (error) {
      console.error("Erreur suppression groupe :", error);
      toast.error("Erreur lors de la suppression du groupe.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleClearGroupMembers = async (group) => {
    if (!group?.id) return;

    const members = toNumber(group.nombreAdherents, 0);

    if (members === 0) {
      toast.info("Ce groupe est déjà vide.");
      return;
    }

    const confirmed = window.confirm(
      `Ce groupe contient ${members} adhérent(s).\nSouhaitez-vous vraiment le vider ?`
    );

    if (!confirmed) return;

    try {
      setClearingId(group.id);

      await clearMembersGroup(group.id);

      toast.success("Groupe vidé avec succès.");
      await fetchGroups();
    } catch (error) {
      console.error("Erreur vidage groupe :", error);
      toast.error("Erreur lors du vidage du groupe.");
    } finally {
      setClearingId(null);
    }
  };

  const handleToggleActive = async (group) => {
    if (!group?.id) return;

    try {
      setActivatingId(group.id);

      await activateGroup(group.id);

      toast.success("Statut du groupe mis à jour.");
      await fetchGroups();
    } catch (error) {
      console.error("Erreur statut groupe :", error);
      toast.error("Erreur lors de la mise à jour du statut.");
    } finally {
      setActivatingId(null);
    }
  };

  const moveGroup = async (groupId, direction) => {
    if (reordering || loading) return;

    const orderedGroups = sortGroupsByOrder(groups);
    const scopeGroups = filters.dojoId
      ? orderedGroups.filter((group) => String(group.dojo_id) === String(filters.dojoId))
      : orderedGroups;

    const currentIndex = scopeGroups.findIndex((group) => group.id === groupId);
    if (currentIndex === -1) return;

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= scopeGroups.length) return;

    const current = scopeGroups[currentIndex];
    const target = scopeGroups[targetIndex];

    const nextGroups = orderedGroups.map((group) => {
      if (group.id === current.id) {
        return {
          ...group,
          ordre_groupe: target.ordre_groupe,
        };
      }

      if (group.id === target.id) {
        return {
          ...group,
          ordre_groupe: current.ordre_groupe,
        };
      }

      return group;
    });

    try {
      setReordering(true);
      setGroups(sortGroupsByOrder(nextGroups));

      await updateOrderGroup(nextGroups);

      toast.success("Ordre des groupes mis à jour.");
      await fetchGroups();
    } catch (error) {
      console.error("Erreur ordre groupes :", error);
      toast.error("Erreur lors de la mise à jour de l’ordre.");
      await fetchGroups();
    } finally {
      setReordering(false);
    }
  };

  return {
    groups,
    dojos,
    enseignants,
    filteredGroups,
    loading,
    loadingMeta,

    filters,
    setFilter,
    resetFilters,

    stats,
    filteredStats,
    nextOrder,
    disciplineSuggestions,

    isQuotaActive,
    quotaUpdating,
    handleToggleQuota,

    drawerOpen,
    drawerMode,
    drawerGroup,
    submittingDrawer,
    openCreateDrawer,
    openEditDrawer,
    openDuplicateDrawer,
    closeDrawer,
    handleSubmitGroup,

    deletingId,
    clearingId,
    activatingId,
    reordering,

    handleDeleteGroup,
    handleClearGroupMembers,
    handleToggleActive,
    moveGroup,

    refreshAll,
  };
}